import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from '../shared/logger.service';
import { ERROR_MESSAGES } from '../constants/appointment.constants';

export interface ErrorContext {
  component: string;
  method: string;
  operation: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  constructor(
    private toastr: ToastrService,
    private logger: LoggerService
  ) {}

  /**
   * Trata erros de forma centralizada
   */
  handle(error: any, context?: ErrorContext): void {
    this.logger.error('Error occurred', { error, context });

    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error, context);
    } else if (error instanceof Error) {
      this.handleGenericError(error, context);
    } else {
      this.handleUnknownError(error, context);
    }
  }

  /**
   * Trata erros HTTP
   */
  private handleHttpError(error: HttpErrorResponse, context?: ErrorContext): void {
    const status = error.status;
    const message = error.error?.message || error.message;

    switch (status) {
      case 400:
        this.toastr.error(message || 'Dados inválidos', 'Erro de Validação');
        break;
      case 401:
        this.toastr.error('Sessão expirada. Faça login novamente.', 'Não Autorizado');
        break;
      case 403:
        this.toastr.error('Você não tem permissão para esta ação.', 'Acesso Negado');
        break;
      case 404:
        this.toastr.error('Recurso não encontrado.', 'Não Encontrado');
        break;
      case 409:
        this.toastr.error('Conflito de dados. Verifique as informações.', 'Conflito');
        break;
      case 422:
        this.toastr.error(message || 'Dados inválidos', 'Erro de Validação');
        break;
      case 429:
        this.toastr.error('Muitas tentativas. Tente novamente em alguns minutos.', 'Rate Limit');
        break;
      case 500:
        this.toastr.error('Erro interno do servidor. Tente novamente.', 'Erro do Servidor');
        break;
      case 502:
      case 503:
      case 504:
        this.toastr.error('Serviço temporariamente indisponível.', 'Serviço Indisponível');
        break;
      default:
        this.toastr.error(message || ERROR_MESSAGES.GENERIC, 'Erro');
    }
  }

  /**
   * Trata erros genéricos
   */
  private handleGenericError(error: Error, context?: ErrorContext): void {
    const message = error.message || ERROR_MESSAGES.GENERIC;
    this.toastr.error(message, 'Erro');
  }

  /**
   * Trata erros desconhecidos
   */
  private handleUnknownError(error: any, context?: ErrorContext): void {
    this.toastr.error(ERROR_MESSAGES.GENERIC, 'Erro Inesperado');
  }

  /**
   * Trata erros de validação de formulário
   */
  handleFormValidationErrors(errors: any[], formName: string): void {
    errors.forEach(error => {
      this.toastr.error(error.message, `Erro no ${formName}`);
    });
  }

  /**
   * Trata erros de rede
   */
  handleNetworkError(error: any, context?: ErrorContext): void {
    this.logger.error('Network error', { error, context });
    this.toastr.error(ERROR_MESSAGES.NETWORK, 'Erro de Conexão');
  }

  /**
   * Trata erros de permissão
   */
  handlePermissionError(error: any, context?: ErrorContext): void {
    this.logger.error('Permission error', { error, context });
    this.toastr.error(ERROR_MESSAGES.PERMISSION, 'Acesso Negado');
  }

  /**
   * Trata erros de validação
   */
  handleValidationError(error: any, context?: ErrorContext): void {
    this.logger.error('Validation error', { error, context });
    this.toastr.error(ERROR_MESSAGES.VALIDATION, 'Dados Inválidos');
  }

  /**
   * Trata erros de recurso não encontrado
   */
  handleNotFoundError(error: any, context?: ErrorContext): void {
    this.logger.error('Not found error', { error, context });
    this.toastr.error(ERROR_MESSAGES.NOT_FOUND, 'Não Encontrado');
  }

  /**
   * Trata erros de timeout
   */
  handleTimeoutError(error: any, context?: ErrorContext): void {
    this.logger.error('Timeout error', { error, context });
    this.toastr.error('Operação demorou muito para responder. Tente novamente.', 'Timeout');
  }

  /**
   * Trata erros de rate limit
   */
  handleRateLimitError(error: any, context?: ErrorContext): void {
    this.logger.error('Rate limit error', { error, context });
    this.toastr.error('Muitas tentativas. Aguarde alguns minutos.', 'Rate Limit');
  }

  /**
   * Trata erros de cache
   */
  handleCacheError(error: any, context?: ErrorContext): void {
    this.logger.error('Cache error', { error, context });
    this.toastr.warning('Erro ao carregar dados do cache. Recarregando...', 'Cache');
  }

  /**
   * Trata erros de autenticação
   */
  handleAuthError(error: any, context?: ErrorContext): void {
    this.logger.error('Authentication error', { error, context });
    this.toastr.error('Sessão expirada. Faça login novamente.', 'Autenticação');
  }

  /**
   * Trata erros de autorização
   */
  handleAuthorizationError(error: any, context?: ErrorContext): void {
    this.logger.error('Authorization error', { error, context });
    this.toastr.error('Você não tem permissão para esta ação.', 'Autorização');
  }

  /**
   * Trata erros de dados
   */
  handleDataError(error: any, context?: ErrorContext): void {
    this.logger.error('Data error', { error, context });
    this.toastr.error('Erro ao processar dados.', 'Dados');
  }

  /**
   * Trata erros de API
   */
  handleApiError(error: any, context?: ErrorContext): void {
    this.logger.error('API error', { error, context });
    this.toastr.error('Erro na comunicação com o servidor.', 'API');
  }

  /**
   * Trata erros de validação de dados
   */
  handleDataValidationError(errors: string[], context?: ErrorContext): void {
    this.logger.error('Data validation error', { errors, context });
    errors.forEach(error => {
      this.toastr.error(error, 'Validação');
    });
  }

  /**
   * Trata erros de upload
   */
  handleUploadError(error: any, context?: ErrorContext): void {
    this.logger.error('Upload error', { error, context });
    this.toastr.error('Erro ao fazer upload do arquivo.', 'Upload');
  }

  /**
   * Trata erros de download
   */
  handleDownloadError(error: any, context?: ErrorContext): void {
    this.logger.error('Download error', { error, context });
    this.toastr.error('Erro ao fazer download do arquivo.', 'Download');
  }

  /**
   * Trata erros de impressão
   */
  handlePrintError(error: any, context?: ErrorContext): void {
    this.logger.error('Print error', { error, context });
    this.toastr.error('Erro ao imprimir documento.', 'Impressão');
  }

  /**
   * Trata erros de exportação
   */
  handleExportError(error: any, context?: ErrorContext): void {
    this.logger.error('Export error', { error, context });
    this.toastr.error('Erro ao exportar dados.', 'Exportação');
  }

  /**
   * Trata erros de importação
   */
  handleImportError(error: any, context?: ErrorContext): void {
    this.logger.error('Import error', { error, context });
    this.toastr.error('Erro ao importar dados.', 'Importação');
  }

  /**
   * Trata erros de backup
   */
  handleBackupError(error: any, context?: ErrorContext): void {
    this.logger.error('Backup error', { error, context });
    this.toastr.error('Erro ao fazer backup dos dados.', 'Backup');
  }

  /**
   * Trata erros de restauração
   */
  handleRestoreError(error: any, context?: ErrorContext): void {
    this.logger.error('Restore error', { error, context });
    this.toastr.error('Erro ao restaurar dados.', 'Restauração');
  }

  /**
   * Trata erros de sincronização
   */
  handleSyncError(error: any, context?: ErrorContext): void {
    this.logger.error('Sync error', { error, context });
    this.toastr.error('Erro ao sincronizar dados.', 'Sincronização');
  }

  /**
   * Trata erros de configuração
   */
  handleConfigError(error: any, context?: ErrorContext): void {
    this.logger.error('Config error', { error, context });
    this.toastr.error('Erro na configuração do sistema.', 'Configuração');
  }

  /**
   * Trata erros de licença
   */
  handleLicenseError(error: any, context?: ErrorContext): void {
    this.logger.error('License error', { error, context });
    this.toastr.error('Erro de licença do sistema.', 'Licença');
  }

  /**
   * Trata erros de atualização
   */
  handleUpdateError(error: any, context?: ErrorContext): void {
    this.logger.error('Update error', { error, context });
    this.toastr.error('Erro ao atualizar sistema.', 'Atualização');
  }

  /**
   * Trata erros de instalação
   */
  handleInstallError(error: any, context?: ErrorContext): void {
    this.logger.error('Install error', { error, context });
    this.toastr.error('Erro na instalação do sistema.', 'Instalação');
  }

  /**
   * Trata erros de desinstalação
   */
  handleUninstallError(error: any, context?: ErrorContext): void {
    this.logger.error('Uninstall error', { error, context });
    this.toastr.error('Erro na desinstalação do sistema.', 'Desinstalação');
  }

  /**
   * Trata erros de migração
   */
  handleMigrationError(error: any, context?: ErrorContext): void {
    this.logger.error('Migration error', { error, context });
    this.toastr.error('Erro na migração de dados.', 'Migração');
  }

  /**
   * Trata erros de rollback
   */
  handleRollbackError(error: any, context?: ErrorContext): void {
    this.logger.error('Rollback error', { error, context });
    this.toastr.error('Erro no rollback de dados.', 'Rollback');
  }
}
